import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Grid,
  Typography,
  Paper,
  Tabs,
  Tab,
  Card,
  CardContent,
  Chip,
  Divider,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Payment,
  CheckCircle,
  HourglassEmpty,
  LocationOn,
  CalendarToday,
  AttachMoney,
  ExpandMore,
} from '@mui/icons-material';
import { Layout, UserNavbar, Loading } from '../../../components';
import {
  getVendorUnpaidRetailBookings,
  getVendorPaidRetailBookings,
  createRetailCheckoutSession,
} from '../../../core/vendor-retail/action';

function RetailPayments() {
  const dispatch = useDispatch();
  const [currentTab, setCurrentTab] = useState(0);
  const [processingPayments, setProcessingPayments] = useState(new Set());

  // Get data from Redux
  const { user } = useSelector((store) => store.users);
  const { unpaidBookings, paidBookings, loading } = useSelector((store) => store.vendorRetail);

  // Fetch unpaid and paid bookings on mount
  useEffect(() => {
    if (user?.id) {
      dispatch(getVendorUnpaidRetailBookings(user.id));
      dispatch(getVendorPaidRetailBookings(user.id));
    }
  }, [dispatch, user?.id]);

  // Helper function to convert 24-hour time to 12-hour AM/PM format
  const formatTime12Hour = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12; // Convert 0 to 12 for midnight
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Sort bookings by date and time (closest to now first, then earliest time first)
  const sortBookingsByDate = (bookings) =>
    [...bookings].sort((a, b) => {
      // First, compare dates
      const dateA = new Date(a.booking_date);
      const dateB = new Date(b.booking_date);
      const dateDiff = dateA - dateB;

      // If dates are different, sort by date
      if (dateDiff !== 0) {
        return dateDiff;
      }

      // If dates are the same, sort by start time
      const timeA = a.start_time || '00:00';
      const timeB = b.start_time || '00:00';
      return timeA.localeCompare(timeB);
    });

  // Group bookings by venue and date
  const groupBookings = (bookings) => {
    const sorted = sortBookingsByDate(bookings);
    const groups = {};

    sorted.forEach((booking) => {
      const key = `${booking.retail_schedule_id}-${booking.booking_date}`;
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(booking);
    });

    return Object.values(groups);
  };

  // Filter and group bookings by application status
  const approvedApplications = groupBookings(
    unpaidBookings.filter((booking) => booking.application_status === 'approved'),
  );

  const inProgressApplications = groupBookings(
    unpaidBookings.filter((booking) =>
      ['incomplete', 'pending'].includes(booking.application_status),
    ),
  );

  const paidApplications = groupBookings(paidBookings || []);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handlePayNow = (booking) => {
    // Mark this booking as processing
    setProcessingPayments((prev) => new Set(prev).add(booking.id));

    console.log('🎯 [Pay Now Clicked] ============================================');
    console.log('📦 Booking Data:', booking);
    console.log('💰 Amount to charge:', booking.total_price);
    console.log('👤 Vendor ID:', user.id);
    console.log(
      '🏢 Venue:',
      booking?.retail_schedule?.retail_profile?.venue?.name || booking?.venue?.name,
    );
    console.log('📅 Booking Date:', booking.booking_date);
    console.log('⏰ Time:', `${booking.start_time} - ${booking.end_time}`);
    // Prepare checkout session data
    const checkoutData = {
      vendor_info: {
        name: user.name,
        email: user.email,
        phone: user.phone || '',
      },
      payment_info: {
        currency: 'usd',
        unit_amount: parseFloat(booking.total_price), // Amount in dollars
      },
      metadata: {
        vendorId: user.id,
        bookingId: booking.id,
        retailScheduleId: booking.retail_schedule_id,
        isFullPayment: 'true',
      },
    };

    console.log('📤 Sending to Stripe:', checkoutData);
    console.log('🔑 Metadata being sent:', checkoutData.metadata);
    console.log('================================================================');

    // Dispatch the action to create checkout session
    dispatch(createRetailCheckoutSession(checkoutData));
  };

  const handlePayAll = (bookingGroup) => {
    // Mark all bookings in group as processing
    const groupIds = bookingGroup.map((b) => b.id);
    setProcessingPayments((prev) => {
      const newSet = new Set(prev);
      groupIds.forEach((id) => newSet.add(id));
      return newSet;
    });

    console.log('💳 [Pay All Clicked] ============================================');
    console.log(`📋 Number of bookings: ${bookingGroup.length}`);
    console.log('📦 Booking IDs:', groupIds);

    // Calculate total amount for all bookings
    const totalAmount = bookingGroup.reduce(
      (sum, booking) => sum + parseFloat(booking.total_price || 0),
      0,
    );

    // Create comma-separated list of booking IDs
    const batchBookingIds = groupIds.join(',');

    console.log('💰 Total amount to charge:', totalAmount);
    console.log('🔗 Batch booking IDs:', batchBookingIds);

    // Use first booking for venue/schedule info
    const firstBooking = bookingGroup[0];

    // Prepare batch checkout session data
    const checkoutData = {
      vendor_info: {
        name: user.name,
        email: user.email,
        phone: user.phone || '',
      },
      payment_info: {
        currency: 'usd',
        unit_amount: totalAmount, // Combined total
      },
      metadata: {
        vendorId: user.id,
        bookingId: firstBooking.id, // Primary booking ID
        batchBookingIds, // All booking IDs as comma-separated string
        isBatchPayment: 'true', // Flag to indicate this is a batch payment
        retailScheduleId: firstBooking.retail_schedule_id,
        isFullPayment: 'true',
      },
    };

    console.log('📤 Sending batch payment to Stripe:', checkoutData);
    console.log('🔑 Batch metadata being sent:', checkoutData.metadata);
    console.log('================================================================');

    // Dispatch the action to create checkout session
    dispatch(createRetailCheckoutSession(checkoutData));
  };

  // Render a grouped set of bookings (for same venue and date)
  const renderGroupedApplicationCard = (bookingGroup) => {
    if (!bookingGroup || bookingGroup.length === 0) return null;

    // Use first booking for summary info (all bookings in group share venue and date)
    const firstBooking = bookingGroup[0];
    const venueName =
      firstBooking?.retail_schedule?.retail_profile?.venue?.name ||
      firstBooking?.venue?.name ||
      'Unknown Venue';
    const retailSpaceName = firstBooking?.retail_schedule?.retail_profile?.name || 'Retail Space';
    const city = firstBooking?.venue?.city?.name || 'Unknown City';
    const state =
      firstBooking?.venue?.state?.code || firstBooking?.venue?.state?.name || 'Unknown State';
    const bookingDate = firstBooking?.booking_date || '';
    const status = firstBooking?.application_status || 'unknown';
    const paymentStatus = firstBooking?.payment_status || 'unpaid';
    const isPaid = paymentStatus === 'paid';

    // Calculate total hours and amount for all bookings in group
    const totalHours = bookingGroup.reduce(
      (sum, booking) => sum + (parseFloat(booking?.duration_hours) || 0),
      0,
    );
    const totalAmount = bookingGroup.reduce(
      (sum, booking) => sum + (parseFloat(booking?.total_price) || 0),
      0,
    );

    // Get time range (earliest start to latest end)
    const timeSlots = bookingGroup
      .map((b) => `${formatTime12Hour(b.start_time)} - ${formatTime12Hour(b.end_time)}`)
      .join(', ');

    // Determine display label
    const getStatusLabel = (statusValue, isPaidStatus) => {
      if (isPaidStatus) return 'Paid';
      if (statusValue === 'approved') return 'Approved - Awaiting Payment';
      if (['incomplete', 'pending'].includes(statusValue)) return 'Pending';
      return statusValue.charAt(0).toUpperCase() + statusValue.slice(1);
    };

    return (
      <Accordion
        key={`${firstBooking.retail_schedule_id}-${firstBooking.booking_date}`}
        elevation={2}
        sx={{
          mb: 3,
          borderRadius: 2,
          '&:before': { display: 'none' },
          '&.Mui-expanded': { margin: '0 0 24px 0' },
        }}>
        <AccordionSummary
          expandIcon={<ExpandMore sx={{ color: '#EE7B57' }} />}
          sx={{
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
          }}>
          <Box sx={{ width: '100%', pr: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
              <Box>
                <Typography variant="h5" fontWeight="bold" color="#EE7B57">
                  {venueName}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  {retailSpaceName}
                </Typography>
              </Box>
              <Chip
                icon={
                  isPaid || status === 'approved' ? (
                    <CheckCircle sx={{ fontSize: '18px !important' }} />
                  ) : (
                    <HourglassEmpty sx={{ fontSize: '18px !important' }} />
                  )
                }
                label={getStatusLabel(status, isPaid)}
                color={isPaid || status === 'approved' ? 'success' : 'warning'}
                sx={{ fontWeight: 600 }}
              />
            </Box>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={4}>
                <Box display="flex" alignItems="center" gap={1}>
                  <LocationOn sx={{ color: '#EE7B57', fontSize: 20 }} />
                  <Typography variant="body2">
                    {city}, {state}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box display="flex" alignItems="center" gap={1}>
                  <CalendarToday sx={{ color: '#EE7B57', fontSize: 20 }} />
                  <Typography variant="body2">
                    {bookingDate ? new Date(bookingDate).toLocaleDateString() : 'TBD'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box display="flex" alignItems="center" gap={1}>
                  <AttachMoney sx={{ color: '#EE7B57', fontSize: 20 }} />
                  <Typography variant="body2" fontWeight="bold" color="#EE7B57">
                    ${totalAmount.toLocaleString()} ({totalHours} hrs)
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {bookingGroup.length > 1 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                {bookingGroup.length} time slots
              </Typography>
            )}
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ pt: 0 }}>
          <Divider sx={{ mb: 2 }} />

          {!isPaid && status === 'approved' && bookingGroup.length > 1 && (
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => handlePayAll(bookingGroup)}
                disabled={bookingGroup.some((b) => processingPayments.has(b.id))}
                sx={{
                  backgroundColor: '#EE7B57',
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  '&:hover': {
                    backgroundColor: '#d66a47',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#ccc',
                    color: '#666',
                  },
                }}>
                {bookingGroup.some((b) => processingPayments.has(b.id))
                  ? 'Processing...'
                  : `Pay All (${bookingGroup.length} slots - $${totalAmount.toLocaleString()})`}
              </Button>
            </Box>
          )}

          {bookingGroup.map((booking, index) => (
            <Box
              key={booking.id}
              sx={{
                p: 2,
                mb: index < bookingGroup.length - 1 ? 2 : 0,
                backgroundColor: '#f9f9f9',
                borderRadius: 1,
                border: '1px solid #e0e0e0',
              }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <CalendarToday sx={{ color: '#EE7B57', fontSize: 20 }} />
                    <Typography variant="body1" fontWeight={500}>
                      {formatTime12Hour(booking.start_time)} - {formatTime12Hour(booking.end_time)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ({booking.duration_hours} hrs)
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <AttachMoney sx={{ color: '#EE7B57', fontSize: 20 }} />
                    <Typography variant="body1" fontWeight="bold" color="#EE7B57">
                      ${parseFloat(booking.total_price).toLocaleString()}
                    </Typography>
                  </Box>
                </Grid>
                {!isPaid && status === 'approved' && (
                  <Grid item xs={12} sm={3}>
                    <Button
                      variant="contained"
                      onClick={() => handlePayNow(booking)}
                      disabled={processingPayments.has(booking.id)}
                      fullWidth
                      sx={{
                        backgroundColor: '#EE7B57',
                        color: 'white',
                        textTransform: 'none',
                        fontWeight: 600,
                        '&:hover': {
                          backgroundColor: '#d66a47',
                        },
                        '&.Mui-disabled': {
                          backgroundColor: '#ccc',
                          color: '#666',
                        },
                      }}>
                      {processingPayments.has(booking.id) ? 'Processing...' : 'Pay Now'}
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Layout>
      {loading && <Loading />}
      <UserNavbar />
      <Grid
        container
        marginTop={9}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 4 }}>
        <Grid item xs={12} lg={10}>
          {/* Header */}
          <Box sx={{ mb: 4, mt: 2 }}>
            <Box display="flex" alignItems="center" gap={2} mb={1}>
              <Payment sx={{ fontSize: 40, color: '#EE7B57' }} />
              <Typography variant="h3" fontWeight="bold" color="#EE7B57">
                Retail Space Payments
              </Typography>
            </Box>
            <Typography variant="body1" color="text.secondary" sx={{ ml: 7 }}>
              Manage payments for your retail space applications
            </Typography>
          </Box>

          {/* Tabs */}
          <Paper elevation={2} sx={{ borderRadius: 2, overflow: 'hidden', mb: 3 }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                backgroundColor: '#fff',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#666',
                  py: 2,
                  '&.Mui-selected': {
                    color: '#EE7B57',
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#EE7B57',
                  height: 3,
                },
              }}>
              <Tab
                icon={<CheckCircle />}
                iconPosition="start"
                label={`Awaiting Payment (${approvedApplications.length})`}
              />
              <Tab
                icon={<HourglassEmpty />}
                iconPosition="start"
                label={`In Progress (${inProgressApplications.length})`}
              />
              <Tab
                icon={<Payment />}
                iconPosition="start"
                label={`Paid (${paidApplications.length})`}
              />
            </Tabs>
          </Paper>

          {/* Content */}
          <Box>
            {currentTab === 0 && (
              <Box>
                {approvedApplications.length > 0 ? (
                  <>
                    <Typography variant="h6" fontWeight="bold" mb={2} color="#EE7B57">
                      Approved Applications Ready for Payment
                    </Typography>
                    {approvedApplications.map((group) => renderGroupedApplicationCard(group))}
                  </>
                ) : (
                  <Paper elevation={2} sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
                    <CheckCircle sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No approved applications yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      When your retail space applications are approved, they will appear here.
                    </Typography>
                  </Paper>
                )}
              </Box>
            )}

            {currentTab === 1 && (
              <Box>
                {inProgressApplications.length > 0 ? (
                  <>
                    <Typography variant="h6" fontWeight="bold" mb={2} color="#EE7B57">
                      Applications In Progress
                    </Typography>
                    {inProgressApplications.map((group) => renderGroupedApplicationCard(group))}
                  </>
                ) : (
                  <Paper elevation={2} sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
                    <HourglassEmpty sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No applications in progress
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Your pending and incomplete applications will appear here.
                    </Typography>
                  </Paper>
                )}
              </Box>
            )}

            {currentTab === 2 && (
              <Box>
                {paidApplications.length > 0 ? (
                  <>
                    <Typography variant="h6" fontWeight="bold" mb={2} color="#EE7B57">
                      Paid Bookings
                    </Typography>
                    {paidApplications.map((group) => renderGroupedApplicationCard(group))}
                  </>
                ) : (
                  <Paper elevation={2} sx={{ p: 6, textAlign: 'center', borderRadius: 2 }}>
                    <Payment sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No paid bookings yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Your completed payments will appear here.
                    </Typography>
                  </Paper>
                )}
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Layout>
  );
}

export default RetailPayments;
